import { parseServerDate } from './server-date';
// Hàm thuần cho quảng cáo livestream: giá, kiểm tra nhập liệu, nhịp OBS, xoay thẻ.

export interface RateCard {
  liveMonthlyPriceVnd?: number | null;
  liveHourlyPriceVnd?: number | null;
}

/** Tổng tiền phần livestream. Gói tháng lấy nguyên giá gói; gói giờ = đơn giá × số giờ. */
export function liveTotalPrice(card: RateCard, plan: 'Monthly' | 'Hourly', hours?: number): number {
  if (plan === 'Monthly') return card.liveMonthlyPriceVnd ?? 0;
  return (card.liveHourlyPriceVnd ?? 0) * (hours ?? 0);
}

// Cùng danh sách host với phía máy chủ.
const LIVE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'tiktok.com',
  'www.tiktok.com',
  'vm.tiktok.com',
  'facebook.com',
  'www.facebook.com',
  'm.facebook.com',
  'fb.watch',
]);

export function validateLiveUrl(url: string): string | null {
  const msg = 'Link buổi live chỉ nhận YouTube, TikTok hoặc Facebook.';
  const v = (url || '').trim();
  if (!v || v.length > 500) return msg;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return msg;
  }
  if (u.protocol !== 'https:' || !LIVE_HOSTS.has(u.hostname.toLowerCase())) return msg;
  return null;
}

export function validateCard(c: { logoUrl?: string; title: string; line?: string }): string | null {
  if (!c.logoUrl) return 'Cần logo cho thẻ quảng cáo.';
  const title = (c.title || '').trim();
  if (title.length < 1 || title.length > 32) return 'Tiêu đề thẻ từ 1 đến 32 ký tự.';
  if ((c.line || '').trim().length > 60) return 'Dòng mô tả tối đa 60 ký tự.';
  return null;
}

/** 5400 ⇒ "1,5 giờ"; 0 ⇒ "0 giờ". Làm tròn 1 chữ số thập phân, dấu phẩy kiểu Việt. */
export function hoursLabel(seconds: number): string {
  const h = Math.round((Math.max(0, seconds) / 3600) * 10) / 10;
  return `${String(h).replace('.', ',')} giờ`;
}

/** Chỉ báo nhịp khi có OBS, đang phát sóng và nguồn đang hiện. */
export function shouldBeat(obs: { present: boolean; streaming: boolean; visible: boolean }): boolean {
  return obs.present && obs.streaming && obs.visible;
}

export function nextRotationIndex(current: number, count: number): number {
  if (count <= 0) return 0;
  return (current + 1) % count;
}

export function freeSeatsLabel(free: number, max: number): string {
  return free <= 0 ? 'Hết chỗ' : `Còn ${free}/${max} chỗ`;
}

// ── Đơn đặt quảng cáo: trạng thái form ⇒ thân gửi lên máy chủ ──

export interface BookingMedia {
  id: string;
  url: string;
}

export interface BookingFormState {
  creatorId: string;
  adAccountId?: string | null;
  destinationUrl: string;
  withPost: boolean;
  caption: string;
  images: BookingMedia[];
  videos: BookingMedia[];
  holdDays: number;
  withLive: boolean;
  plan: 'Monthly' | 'Hourly';
  hours: number;
  validDays: number;
  slotId: string;
  logo: BookingMedia | null;
  cardTitle: string;
  cardLine: string;
}

export const BOOKING_VALID_DAYS = [30, 60, 90] as const;
export const BOOKING_MAX_HOURLY_HOURS = 500;

function isHttps(url: string): boolean {
  try {
    const u = new URL(url.trim());
    return u.protocol === 'https:' && !!u.hostname;
  } catch {
    return false;
  }
}

/** Kiểm form đặt quảng cáo, trả câu lỗi đầu tiên hoặc null. Câu chữ khớp máy chủ. */
export function validateBookingForm(
  f: BookingFormState,
  card: RateCard & { minHours?: number; liveMonthlyHours?: number | null }
): string | null {
  if (!f.withPost && !f.withLive) return 'Đơn cần có phần đăng bài hoặc livestream.';
  if (!isHttps(f.destinationUrl) || f.destinationUrl.trim().length > 500)
    return 'Link đích phải bắt đầu bằng https://.';
  if (f.withPost) {
    if (!f.caption.trim()) return 'Bạn chưa nhập nội dung.';
    if (f.caption.length > 2000) return 'Nội dung tối đa 2000 ký tự.';
    if (f.images.length + f.videos.length === 0) return 'Cần ít nhất 1 ảnh hoặc video.';
    if (f.holdDays < 7 || f.holdDays > 30) return 'Thời gian giữ bài từ 7 đến 30 ngày.';
  }
  if (f.withLive) {
    if (f.plan === 'Hourly') {
      const min = card.minHours ?? 1;
      if (!Number.isInteger(f.hours) || f.hours < min || f.hours > BOOKING_MAX_HOURLY_HOURS)
        return `Cần mua tối thiểu ${min} giờ.`;
    }
    if (f.plan === 'Hourly' && !(BOOKING_VALID_DAYS as readonly number[]).includes(f.validDays))
      return 'Thời hạn chỉ chọn 30, 60 hoặc 90 ngày.';
    if (!f.slotId) return 'Hãy chọn vị trí quảng cáo.';
    const cardErr = validateCard({ logoUrl: f.logo?.url, title: f.cardTitle, line: f.cardLine });
    if (cardErr) return cardErr;
  }
  return null;
}

/** Dựng thân yêu cầu tạo đơn; phần không chọn thì bỏ hẳn. */
export function buildBookingBody(f: BookingFormState) {
  return {
    adAccountId: f.adAccountId || null,
    creatorId: f.creatorId,
    destinationUrl: f.destinationUrl.trim(),
    post: f.withPost
      ? {
          caption: f.caption.trim(),
          images: f.images,
          videos: f.videos,
          holdDays: f.holdDays,
        }
      : null,
    live: f.withLive
      ? {
          plan: f.plan,
          hours: f.plan === 'Hourly' ? f.hours : null,
          validDays: f.plan === 'Hourly' ? f.validDays : 30,
          slotId: f.slotId,
          cardLogo: f.logo,
          cardTitle: f.cardTitle.trim(),
          cardLine: f.cardLine.trim() || null,
        }
      : null,
  };
}

/** Tổng tiền cả đơn theo bảng giá hiện tại. */
export function bookingTotal(f: BookingFormState, card: RateCard & { postPriceVnd?: number | null }): number {
  return (f.withPost ? card.postPriceVnd ?? 0 : 0) + (f.withLive ? liveTotalPrice(card, f.plan, f.hours) : 0);
}

/** Nhãn trạng thái từng phần của đơn (bài hoặc livestream). */
export function bookingPartStatusLabel(status: string): string {
  switch (status) {
    case 'PendingApproval':
      return 'Chờ admin duyệt';
    case 'Invited':
      return 'Chờ creator trả lời';
    case 'Airing':
      return 'Đang chạy';
    case 'Completed':
      return 'Hoàn thành';
    case 'Declined':
      return 'Creator từ chối';
    case 'Expired':
      return 'Hết hạn trả lời';
    case 'Cancelled':
      return 'Đã huỷ';
    default:
      return status;
  }
}

// ── Huỷ hợp tác, báo gian lận, giải trình ──

export const LIVE_REASON_MIN = 10;
export const LIVE_REASON_MAX = 1000;
export const LIVE_RESPONSE_MAX = 2000;
export const BUSINESS_CANCEL_REASON_MIN = 10;
export const BUSINESS_CANCEL_REASON_MAX = 500;

export const LIVE_CANCEL_REASON_LABEL: Record<string, string> = {
  BrandScandal: 'Doanh nghiệp có phốt hoặc vi phạm',
  Other: 'Lý do khác',
};

export const LIVE_OTHER_CANCEL_WARNING =
  'Huỷ với lý do khác thì bạn không nhận thêm tiền, phần tiền đang giữ sẽ hoàn cho doanh nghiệp.';

const LIVE_STOP_REASON_LABEL: Record<string, string> = {
  Exhausted: 'Đã chạy đủ giờ',
  Expired: 'Hết hạn gói',
  CreatorCancelled: 'Creator đã dừng',
  BusinessCancelled: 'Doanh nghiệp đã huỷ',
  FraudStop: 'Dừng do gian lận',
};

export function liveStopReasonLabel(code?: string | null): string {
  if (!code) return '';
  return LIVE_STOP_REASON_LABEL[code] ?? code;
}

const LIVE_VOID_REASON_LABEL: Record<string, string> = {
  Fraud: 'Gian lận',
  NoLink: 'Thiếu link buổi live',
};

export function liveVoidReasonLabel(code?: string | null): string {
  if (!code) return '';
  return LIVE_VOID_REASON_LABEL[code] ?? code;
}

const REPORT_STATUS_LABEL: Record<string, string> = {
  Open: 'Đang chờ xử lý',
  Upheld: 'Gian lận được xác nhận',
  Dismissed: 'Không đủ căn cứ',
};

export function reportStatusLabel(code?: string | null): string {
  if (!code) return '';
  return REPORT_STATUS_LABEL[code] ?? code;
}

/** Lý do (đã cắt khoảng trắng) phải có độ dài trong [min, max]; trả câu lỗi hoặc null. */
export function validateReason(text: string, min = LIVE_REASON_MIN, max = LIVE_REASON_MAX): string | null {
  const n = (text || '').trim().length;
  return n < min || n > max ? `Lý do từ ${min} đến ${max} ký tự.` : null;
}

export function validateResponse(text: string): string | null {
  const n = (text || '').trim().length;
  if (n < 1) return 'Hãy nhập phần giải trình.';
  return n > LIVE_RESPONSE_MAX ? 'Phần giải trình tối đa 2000 ký tự.' : null;
}

/** Câu cảnh báo khi creator chọn lý do huỷ; chỉ lý do khác mới có. */
export function cancelReasonWarning(code: string): string | null {
  return code === 'Other' ? LIVE_OTHER_CANCEL_WARNING : null;
}

/** Hạn báo gian lận = lúc đóng buổi + số ngày giữ. */
export function reportDeadline(endedAt: string | Date, holdDays: number): Date {
  const start = typeof endedAt === 'string' ? parseServerDate(endedAt) : endedAt;
  return new Date((start?.getTime() ?? NaN) + holdDays * 86400000);
}

/** "Còn 3 ngày để báo" / "Còn 5 giờ để báo" / "Đã quá hạn báo gian lận cho buổi này." */
export function reportDeadlineLabel(endedAt: string | Date, holdDays: number, now: Date = new Date()): string {
  const ms = reportDeadline(endedAt, holdDays).getTime() - now.getTime();
  if (ms <= 0) return 'Đã quá hạn báo gian lận cho buổi này.';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `Còn ${days} ngày để báo`;
  const hours = Math.max(1, Math.ceil(ms / 3600000));
  return `Còn ${hours} giờ để báo`;
}

/** Creator còn giải trình được: báo cáo đang chờ, chưa giải trình, chưa quá hạn. */
export function canRespond(r: { status: string; creatorResponse?: string | null; responseDueAt: string }, now: Date = new Date()): boolean {
  return r.status === 'Open' && !r.creatorResponse && new Date(r.responseDueAt).getTime() > now.getTime();
}
